/**
 * `createApiAnalyticsSource` — the `metadata-registry-api` *saved dashboards*
 * port over the shared `ApiClient`.
 *
 * A saved dashboard is the persisted, Datadog-style document (panels + queries +
 * layout) the analytics feature renders. The registry resolves it across the
 * `system → customer → org → tenant → user` ladder on read and gates authoring
 * by scope; this adapter is the one place the dashboard app turns those routes
 * into typed `Dashboard` documents. The document schema itself lives in
 * `@authe/fe-commons/metadata-analytics` (a Zod model) — we `parseDashboard`
 * every blob so a corrupt/legacy row never reaches the grid unchecked.
 *
 * Panel DATA is NOT fetched here: `@authe/analytics-widgets`' `DashboardView`
 * owns per-panel fetching (via ui-core `useQuery` over the same `ApiClient`), so
 * this port is scoped to the dashboard documents' CRUD. Canonical
 * `/metadata-dashboards*` paths are mapped to `internal/metadata-registry-api`
 * by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  type Dashboard,
  configPaths,
  parseDashboard,
} from "@authe/fe-commons/metadata-analytics";

/** Ladder rung a dashboard is authored at (mirrors the backend `FacetVisibility`). */
export type DashboardScope = Dashboard["scope"];

export interface SaveDashboardOptions {
  /** Author at a specific rung; omitted => the document's own `scope` (default `tenant`). */
  scope?: DashboardScope;
}

export interface DashboardList {
  dashboards: Dashboard[];
  /**
   * Stored rows that failed the document schema and were dropped.
   *
   * Dropping them is right — a half-parsed board renders panels whose queries
   * nobody validated — but the COUNT has to survive the drop. It went to
   * `console.warn` and nowhere else, so a schema change that invalidated every
   * stored board emptied this list in silence, and the page then said "No saved
   * dashboards yet. Open a preset above… or save your own dashboard to see it
   * here." The boards were there. The advice was to make more of them.
   */
  unreadable: number;
}

export interface AnalyticsSource {
  /** Every dashboard resolved for the caller's tenancy (system presets → user boards). */
  listDashboards(): Promise<DashboardList>;
  /** One resolved dashboard document by id. */
  getDashboard(id: string): Promise<Dashboard>;
  /** Create or replace a dashboard (POST is create-or-update server-side). */
  saveDashboard(doc: Dashboard, options?: SaveDashboardOptions): Promise<Dashboard>;
  /** Delete a dashboard by id at a rung (default `tenant`). */
  deleteDashboard(id: string, scope?: DashboardScope): Promise<void>;
}

/** Wire envelope for the collection read (`{ items, next_cursor }`). */
interface DashboardListWire {
  items?: unknown[];
  next_cursor?: string | null;
}

/** Wire envelope for a single-document read/write (`{ dashboard }`). */
interface DashboardItemWire {
  dashboard?: unknown;
}

export function createApiAnalyticsSource(client: ApiClient): AnalyticsSource {
  return {
    async listDashboards(): Promise<DashboardList> {
      const page = unwrapData(await client.get<DashboardListWire>(configPaths.dashboards()));
      const rows = page?.items ?? [];
      // A single malformed row must not blank the whole list — parse
      // defensively and drop only the ones that fail the schema (logged for
      // triage). The count comes back with them: a console line is not a
      // disclosure, and an empty list with a reason nobody can see is the
      // failure this whole surface keeps producing.
      //
      // The route itself is complete, so there is no cursor to follow here:
      // `_read_scoped` walks every page of every ladder rung with `query_iter`
      // before merging by precedence. The `next_cursor` on the envelope is
      // always null.
      const dashboards: Dashboard[] = [];
      let unreadable = 0;
      for (const row of rows) {
        try {
          dashboards.push(parseDashboard(row));
        } catch (err) {
          unreadable += 1;
          console.warn("[analytics] skipping unparseable dashboard row", err);
        }
      }
      return { dashboards, unreadable };
    },

    async getDashboard(id: string): Promise<Dashboard> {
      const body = unwrapData(await client.get<DashboardItemWire>(configPaths.dashboard(id)));
      return parseDashboard(body?.dashboard);
    },

    async saveDashboard(doc: Dashboard, options?: SaveDashboardOptions): Promise<Dashboard> {
      const query = options?.scope ? { scope: options.scope } : undefined;
      const body = unwrapData(
        await client.post<DashboardItemWire>(configPaths.dashboards(), doc, { query }),
      );
      const saved = parseDashboard(body?.dashboard);
      client.invalidate(configPaths.dashboards());
      return saved;
    },

    async deleteDashboard(id: string, scope?: DashboardScope): Promise<void> {
      // `unwrapData`: the client resolves an `{ok:false}` envelope, so a bare
      // `await` reported a dashboard deleted on a refusal at that scope.
      unwrapData(
        await client.delete(configPaths.dashboard(id), {
          query: scope ? { scope } : undefined,
        }),
      );
      client.invalidate(configPaths.dashboards());
    },
  };
}
